import React, {
  FC,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { List, Map } from "immutable";
import { OutgoingRowLayoutTier } from "types/outgoing";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import {
  HeaderWrapper,
  ParcelsWrapper,
} from "../../../../styles/parcelsStyles";
import { getOutgoingLayoutTier } from "../rowLayout";
import LoadMoreFooter from "./LoadMoreFooter";
import { ClickableDiv, CollapseWrapper } from "./OutgoingSection.styles";

interface OutgoingSectionProps {
  fetchData: (data: any) => void;
  itemRenderer: (item: any, layoutTier: OutgoingRowLayoutTier) => ReactNode;
  isCollapseOpen?: boolean;
  children: ReactNode;
  data: any;
  countItemsPerSection?: number;
}

interface SectionItemsProps {
  items: any;
  itemRenderer: (item: any, layoutTier: OutgoingRowLayoutTier) => ReactNode;
  // The list's footer, inside the wrapper so it sits right under the rows.
  children?: ReactNode;
}

// Owns the width measurement (like StorageList does) so the ref is attached
// on this component's first render — OutgoingSection itself renders null
// while its bucket is empty, and useContainerQuery only observes the node
// present on mount.
const SectionItems: FC<SectionItemsProps> = ({
  items,
  itemRenderer,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // The width available to the rows changes with both the viewport and the
  // right-panel squeeze, so measure the list itself instead of the screen.
  const layoutTier = useContainerQuery<OutgoingRowLayoutTier>(
    wrapperRef,
    getOutgoingLayoutTier,
    "full",
  );

  return (
    <ParcelsWrapper ref={wrapperRef}>
      {items.map((item: any) => itemRenderer(item, layoutTier))}
      {children}
    </ParcelsWrapper>
  );
};

const OutgoingSection: FC<OutgoingSectionProps> = ({
  data,
  isCollapseOpen = false,
  fetchData,
  children,
  itemRenderer,
  countItemsPerSection = 10,
}) => {
  const [isOpened, setCollapseOpened] = useState(isCollapseOpen);
  const items = data.get("items", List());
  const type = data.get("type");
  const currentPage = data.get("page") || 1;
  // The backend's total header; NaN when it is missing or not a number.
  const total = parseInt(data.get("total", items.size), 10);
  const isLoading = data.get("isLoading", false);
  // Every request this bucket makes. "Show all" asks for page 1 of the whole
  // bucket, which the duck treats as a replacement (Pay all does the same).
  const request = (page: number, perPage: number) =>
    Map({ type, params: { page, per_page: perPage } });

  const sortedItems = useMemo(
    () =>
      items.sort(
        (a: any, b: any) =>
          +new Date(b.get("created_at")) - +new Date(a.get("created_at")),
      ),
    [items],
  );

  useEffect(() => {
    fetchData(request(1, countItemsPerSection));
  }, []);

  if (!items.size) return null;

  return (
    <section>
      <HeaderWrapper>
        <ClickableDiv onClick={() => setCollapseOpened(!isOpened)}>
          {children}
        </ClickableDiv>
      </HeaderWrapper>
      <CollapseWrapper isOpened={isOpened}>
        <SectionItems items={sortedItems} itemRenderer={itemRenderer}>
          <LoadMoreFooter
            shown={sortedItems.size}
            total={total}
            pageSize={countItemsPerSection}
            isLoading={isLoading}
            onLoadMore={() =>
              fetchData(request(currentPage + 1, countItemsPerSection))
            }
            onShowAll={() => fetchData(request(1, total))}
          />
        </SectionItems>
      </CollapseWrapper>
    </section>
  );
};

export default memo(OutgoingSection);
